import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren, ElementRef,
    EventEmitter, HostListener,
    Input,
    Output,
    QueryList,
    TemplateRef,
    ViewChild
} from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";

import {
    ConfirmationService,
    MessageService,
    PrimeTemplate
} from "primeng/api";
import { Table } from "primeng/table";

import { FilterComponent } from "@features/filter";
import { GridPublicBase } from "./classes/grid-public-base";
import { GridCrudManager } from "./classes/grid-crud-manager";
import { GridDataEditor } from "./classes/grid-data-editor";
import { GridDataViewer } from "./classes/grid-data-viewer";
import { GridQueryManager } from "./classes/grid-query-manager";
import { GridExporter } from "./classes/grid-exporter";
import { GridImporter } from "./classes/grid-importer";
import { IGridSettings } from "./interfaces/grid-settings";
import { ITableSettings } from "./interfaces/table-settings";
import { GridColumnTemplateDirective } from "./grid-template.directive";
import { IGridColumn } from "./interfaces/grid-column";
import { GridImportDialogComponent } from "./grid-import-dialog.component";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { map } from "rxjs";
import { SplitButton } from "primeng/splitbutton";

@Component({
    selector: "grid",
    templateUrl: "./grid.component.html",
    styleUrls: ["./grid.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GridComponent extends GridPublicBase implements AfterViewInit {
    @Output() readonly contextMenuSelectionChange: EventEmitter<any> = new EventEmitter();
    @Output() readonly create = new EventEmitter<any>();
    @Output() readonly delete = new EventEmitter<any>();
    @Output() readonly deleteAll = new EventEmitter<void>();
    @Output() readonly firstChange: EventEmitter<number> = new EventEmitter();
    @Output() readonly onClick: EventEmitter<any> = new EventEmitter();
    @Output() readonly onColResize: EventEmitter<any> = new EventEmitter();
    @Output() readonly onColReorder: EventEmitter<any> = new EventEmitter();
    @Output() readonly onContextMenuSelect: EventEmitter<any> = new EventEmitter();
    @Output() readonly onEditCancel: EventEmitter<any> = new EventEmitter();
    @Output() readonly onEditComplete: EventEmitter<any> = new EventEmitter();
    @Output() readonly onEditInit: EventEmitter<any> = new EventEmitter();
    @Output() readonly onFilter: EventEmitter<any> = new EventEmitter();
    @Output() readonly onHeaderCheckboxToggle: EventEmitter<any> = new EventEmitter();
    @Output() readonly onLazyLoad: EventEmitter<any> = new EventEmitter();
    @Output() readonly onPage: EventEmitter<any> = new EventEmitter();
    @Output() readonly onRowCollapse: EventEmitter<any> = new EventEmitter();
    @Output() readonly onRowExpand: EventEmitter<any> = new EventEmitter();
    @Output() readonly onRowReorder: EventEmitter<any> = new EventEmitter();
    @Output() readonly onRowSelect: EventEmitter<any> = new EventEmitter();
    @Output() readonly onRowUnselect: EventEmitter<any> = new EventEmitter();
    @Output() readonly onSort: EventEmitter<any> = new EventEmitter();
    @Output() readonly onStateRestore: EventEmitter<any> = new EventEmitter();
    @Output() readonly onStateSave: EventEmitter<any> = new EventEmitter();
    @Output() readonly outputCellValueChange = new EventEmitter<{row: any, column: IGridColumn}>();
    @Output() readonly rowsChange: EventEmitter<number> = new EventEmitter();
    @Input() selection: any[] | any;
    @Output() readonly selectionChange = new EventEmitter<any[] | any>();
    @Output() readonly sortFunction: EventEmitter<any> = new EventEmitter();
    @Output() readonly update = new EventEmitter<any>();
    @Output() readonly rowClick = new EventEmitter<{ rowData: any, rowIndex: any }>();
    @ViewChild(GridImportDialogComponent, { static: false }) _importDialog: GridImportDialogComponent;
    @ViewChild(Table, { static: true }) _table: Table;
    @ViewChild(SplitButton) private exportButton: SplitButton;

    @ContentChildren(GridColumnTemplateDirective) private _columnTemplates: QueryList<GridColumnTemplateDirective>;
    @ContentChildren(PrimeTemplate) private _templates: QueryList<PrimeTemplate>;

    public isArray = Array.isArray;

    constructor(public _router: Router,
                public _fb: UntypedFormBuilder,
                public _confirmationService: ConfirmationService,
                public _messageService: MessageService,
                private _element: ElementRef,
                private _broadcastService: BroadcastService,
                _cd: ChangeDetectorRef) {
        super(_cd);
    }

    @Input() paginatorDropdownAppendTo: HTMLElement | ElementRef | TemplateRef<any> | string | null | undefined | any;

    @Input() set filter(value: FilterComponent) {
        this._filterComponent = value;

        if (this._viewInitialized) {
            this._queryManager.initFilterComponent();
        }
    }
    get filter(): FilterComponent {
        return this._filterComponent;
    }

    @Input() set gridSettings(value: IGridSettings) {
        if (!value) return;

        Object.assign(this._gridSettings, value);

        this._initGridSettings();

        if (this._viewInitialized) {
            this._checkConfigurationValidity();
            this._cd.detectChanges();
        }
    }
    get gridSettings(): IGridSettings {
        return this._gridSettings;
    }

    @Input() set tableSettings(value: ITableSettings) {
        if (!value) return;

        Object.assign(this._tableSettings, value);

        if (this._viewInitialized) {
            this._setTableSettings();
            this._checkConfigurationValidity();
            this._cd.detectChanges();
        }
    }
    get tableSettings(): ITableSettings {
        return this._tableSettings;
    }

    get count(): number{
        return this._table?.value?.length ?? 0;
    }


    ngAfterViewInit(): void {
        this._viewInitialized = true;

        this._dataViewer = new GridDataViewer(this, this._columnTemplates);
        this._crudManager = new GridCrudManager(this);
        this._dataEditor = new GridDataEditor(this);
        this._queryManager = new GridQueryManager(this, this._columnTemplates);
        this._exporter = new GridExporter(this);
        this._importer = new GridImporter(this);

        setTimeout(() => {
            this._templates.forEach(x => {
                switch (x.name) {
                    case "headergrouped": this._table["headerGroupedTemplate"] = x.template; break;
                    case "loadingbody": this._table["loadingBodyTemplate"] = x.template; break;
                    case "footergrouped": this._table["footerGroupedTemplate"] = x.template; break;
                    case "colgroup": this._table["colGroupTemplate"] = x.template; break;
                    case "rowexpansion": this._table["expandedRowTemplate"] = x.template; break;
                    case "groupheader": this._table["groupHeaderTemplate"] = x.template; break;
                    case "groupfooter": this._table["groupFooterTemplate"] = x.template; break;
                    case "frozenrows": this._table["frozenRowsTemplate"] = x.template; break;
                    case "frozenheader": this._table["frozenHeaderTemplate"] = x.template; break;
                    case "frozenbody": this._table["frozenBodyTemplate"] = x.template; break;
                    case "frozenfooter": this._table["frozenFooterTemplate"] = x.template; break;
                    case "frozencolgroup": this._table["frozenColGroupTemplate"] = x.template; break;
                    case "frozenrowexpansion": this._table["frozenExpandedRowTemplate"] = x.template; break;
                    case "emptymessage": this._table["emptyMessageTemplate"] = x.template; break;
                    case "paginatorleft": this._table["paginatorLeftTemplate"] = x.template; break;
                    case "paginatorright": this._table["paginatorRightTemplate"] = x.template; break;
                    case "paginatordropdownitem": this._table["paginatorDropdownItemTemplate"] = x.template; break;
                    default: this._table[`${x.name}Template`] = x.template; break;
                }
            });

            if (this._tableSettings.lazyLoadOnInit !== false) {
                this._setTableSettings();
                this._checkConfigurationValidity();
            }

            this._cd.detectChanges();
        });
    }

    public onRowClick($event: KeyboardEvent, rowData: any, rowIndex: any): void {
        this.rowClick.emit({
            rowData: rowData,
            rowIndex: rowIndex
        });
    }


    @HostListener("window:beforeprint", ["$event"])
    private _onBeforePrint(): void {
        this._printing = true;

        if (this._table.lazy && this.gridSettings.dataService) {
            const lazyLoadEvent = this._queryManager.getLazyLoadMetadata();
            lazyLoadEvent.first = null;
            lazyLoadEvent.rows = null;
            this._crudManager.loadData(lazyLoadEvent);
        }
    }

    @HostListener("window:afterprint", ["$event"])
    private _onAfterPrint(): void {
        this._printing = false;
        this.reload();
    }

    @HostListener("dragover", ["$event"])
    private _onDragOver(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.gridSettings.importEnabled || !this.gridSettings.importService) return;

        this._element.nativeElement.style.opacity = 0.5;
    }

    @HostListener("dragleave", ["$event"])
    private _onDragLeave(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.gridSettings.importEnabled || !this.gridSettings.importService) return;

        this._element.nativeElement.style.opacity = 1;
    }

    @HostListener("drop", ["$event"])
    private _onDrop(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.gridSettings.importEnabled || !this.gridSettings.importService) return;

        this._element.nativeElement.style.opacity = 1;

        const files = <File[]> Array.from(event.dataTransfer.files);
        if (files?.length) {
            this._importer.setFileForUploader(files[0]);
        }
    }

    get rowDeleteButtonDisabled$() {
        return this._broadcastService
            .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
            .pipe(map(isOnline => !(isOnline || this.gridSettings.deletingOfflineEnabled === true)));
    }

    protected exportClicked(e: MouseEvent) {
        setTimeout(() => this.exportButton.onDropdownButtonClick(e));
    }
    public buildPlusTooltip (
        items: any[],
        plusValueGetter?: (itm: any) => string,
        ): string {
        const names = items.map(itm =>
            plusValueGetter ? plusValueGetter(itm) : String(itm)
        );
        const rows: string[] = [];

        for (let i = 0; i < names.length; i += 2) {
            rows.push(names.slice(i, i + 2).join(",\n"));
        }

        return rows.join(",\n");
    }

    protected truncateFillValueGetter(value: string): string {
        if (!value) return "";
        return value.length > 22
                ? value.slice(0, 19) + "..."
                : value;
    }
}
